import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  ExternalLink,
  Zap,
  DollarSign,
  Layers,
  Filter,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────

type Provider =
  | "OpenAI"
  | "Anthropic"
  | "Google"
  | "Meta"
  | "Mistral"
  | "xAI"
  | "DeepSeek";

type Category = "flagship" | "mid" | "small" | "reasoning" | "embedding";

interface Model {
  id: string;
  name: string;
  provider: Provider;
  category: Category;
  contextWindow: number; // total tokens
  maxOutput: number; // max output tokens
  inputPrice: number; // per 1M tokens
  outputPrice: number; // per 1M tokens
  cachedInputPrice?: number; // per 1M cached input tokens
  releaseDate: string; // YYYY-MM
  multimodal: boolean;
  openSource: boolean;
  noteworthy?: string;
}

// ── Model Database — Feb 2026 (updated) ─────────────────────

const MODELS: Model[] = [
  // ── OpenAI ──
  {
    id: "gpt-5.2",
    name: "GPT-5.2",
    provider: "OpenAI",
    category: "flagship",
    contextWindow: 256_000,
    maxOutput: 32_768,
    inputPrice: 1.75,
    outputPrice: 14,
    cachedInputPrice: 0.175,
    releaseDate: "2026-01",
    multimodal: true,
    openSource: false,
    noteworthy: "Latest frontier model",
  },
  {
    id: "gpt-5.2-pro",
    name: "GPT-5.2 Pro",
    provider: "OpenAI",
    category: "reasoning",
    contextWindow: 256_000,
    maxOutput: 65_536,
    inputPrice: 21,
    outputPrice: 168,
    releaseDate: "2026-01",
    multimodal: true,
    openSource: false,
    noteworthy: "Extended thinking, highest capability",
  },
  {
    id: "gpt-5-mini",
    name: "GPT-5 mini",
    provider: "OpenAI",
    category: "small",
    contextWindow: 128_000,
    maxOutput: 16_384,
    inputPrice: 0.25,
    outputPrice: 2,
    cachedInputPrice: 0.025,
    releaseDate: "2025-10",
    multimodal: true,
    openSource: false,
    noteworthy: "Best price-performance small model",
  },
  {
    id: "gpt-4.1",
    name: "GPT-4.1",
    provider: "OpenAI",
    category: "flagship",
    contextWindow: 1_047_576,
    maxOutput: 32_768,
    inputPrice: 3,
    outputPrice: 12,
    cachedInputPrice: 0.75,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: false,
    noteworthy: "1M context, great for codebases",
  },
  {
    id: "gpt-4.1-mini",
    name: "GPT-4.1 mini",
    provider: "OpenAI",
    category: "mid",
    contextWindow: 1_047_576,
    maxOutput: 32_768,
    inputPrice: 0.8,
    outputPrice: 3.2,
    cachedInputPrice: 0.2,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: false,
  },
  {
    id: "gpt-4.1-nano",
    name: "GPT-4.1 nano",
    provider: "OpenAI",
    category: "small",
    contextWindow: 1_047_576,
    maxOutput: 32_768,
    inputPrice: 0.2,
    outputPrice: 0.8,
    cachedInputPrice: 0.05,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: false,
    noteworthy: "Cheapest 1M context model",
  },
  {
    id: "o3",
    name: "o3",
    provider: "OpenAI",
    category: "reasoning",
    contextWindow: 200_000,
    maxOutput: 100_000,
    inputPrice: 12,
    outputPrice: 48,
    cachedInputPrice: 3,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: false,
    noteworthy: "Top reasoning model",
  },
  {
    id: "o4-mini",
    name: "o4-mini",
    provider: "OpenAI",
    category: "reasoning",
    contextWindow: 200_000,
    maxOutput: 100_000,
    inputPrice: 1.5,
    outputPrice: 6,
    cachedInputPrice: 0.375,
    releaseDate: "2025-07",
    multimodal: true,
    openSource: false,
    noteworthy: "Fast reasoning, great value",
  },

  // ── Anthropic ──
  {
    id: "claude-opus-4.6",
    name: "Claude Opus 4.6",
    provider: "Anthropic",
    category: "flagship",
    contextWindow: 200_000,
    maxOutput: 32_000,
    inputPrice: 5,
    outputPrice: 25,
    cachedInputPrice: 1.25,
    releaseDate: "2026-02",
    multimodal: true,
    openSource: false,
    noteworthy: "Best for complex coding tasks",
  },
  {
    id: "claude-sonnet-4.5",
    name: "Claude Sonnet 4.5",
    provider: "Anthropic",
    category: "mid",
    contextWindow: 200_000,
    maxOutput: 16_384,
    inputPrice: 3,
    outputPrice: 15,
    cachedInputPrice: 0.75,
    releaseDate: "2025-10",
    multimodal: true,
    openSource: false,
    noteworthy: "Balanced speed and quality",
  },
  {
    id: "claude-haiku-4.5",
    name: "Claude Haiku 4.5",
    provider: "Anthropic",
    category: "small",
    contextWindow: 200_000,
    maxOutput: 8_192,
    inputPrice: 1,
    outputPrice: 5,
    cachedInputPrice: 0.1,
    releaseDate: "2025-10",
    multimodal: true,
    openSource: false,
    noteworthy: "Fast and affordable",
  },
  {
    id: "claude-sonnet-4.6",
    name: "Claude Sonnet 4.6",
    provider: "Anthropic",
    category: "mid",
    contextWindow: 200_000,
    maxOutput: 16_384,
    inputPrice: 3,
    outputPrice: 15,
    cachedInputPrice: 0.3,
    releaseDate: "2026-02",
    multimodal: true,
    openSource: false,
    noteworthy: "1M context beta, default for Pro users",
  },

  // ── Google ──
  {
    id: "gemini-2.5-pro",
    name: "Gemini 2.5 Pro",
    provider: "Google",
    category: "flagship",
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    inputPrice: 1.25,
    outputPrice: 10,
    cachedInputPrice: 0.3125,
    releaseDate: "2025-06",
    multimodal: true,
    openSource: false,
    noteworthy: "1M context, built-in thinking",
  },
  {
    id: "gemini-2.5-flash",
    name: "Gemini 2.5 Flash",
    provider: "Google",
    category: "mid",
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    inputPrice: 0.3,
    outputPrice: 2.5,
    cachedInputPrice: 0.075,
    releaseDate: "2025-06",
    multimodal: true,
    openSource: false,
    noteworthy: "Best value for 1M context",
  },
  {
    id: "gemini-2.5-flash-lite",
    name: "Gemini 2.5 Flash Lite",
    provider: "Google",
    category: "small",
    contextWindow: 1_048_576,
    maxOutput: 8_192,
    inputPrice: 0.1,
    outputPrice: 0.4,
    releaseDate: "2026-01",
    multimodal: true,
    openSource: false,
    noteworthy: "Cheapest 1M context",
  },
  {
    id: "gemini-3.1-pro",
    name: "Gemini 3.1 Pro",
    provider: "Google",
    category: "flagship",
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    inputPrice: 2,
    outputPrice: 12,
    cachedInputPrice: 0.5,
    releaseDate: "2026-02",
    multimodal: true,
    openSource: false,
    noteworthy: "Latest Google flagship, 1M context",
  },
  {
    id: "gemini-3-flash",
    name: "Gemini 3 Flash",
    provider: "Google",
    category: "mid",
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    inputPrice: 0.5,
    outputPrice: 3,
    releaseDate: "2025-12",
    multimodal: true,
    openSource: false,
    noteworthy: "Next-gen fast model",
  },

  // ── Meta ──
  {
    id: "llama-4-maverick",
    name: "Llama 4 Maverick",
    provider: "Meta",
    category: "flagship",
    contextWindow: 1_048_576,
    maxOutput: 32_768,
    inputPrice: 0.2,
    outputPrice: 0.6,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: true,
    noteworthy: "Open-source, 128 experts MoE",
  },
  {
    id: "llama-4-scout",
    name: "Llama 4 Scout",
    provider: "Meta",
    category: "mid",
    contextWindow: 512_000,
    maxOutput: 32_768,
    inputPrice: 0.1,
    outputPrice: 0.3,
    releaseDate: "2025-04",
    multimodal: true,
    openSource: true,
    noteworthy: "Open-source, 16 experts MoE",
  },

  // ── Mistral ──
  {
    id: "mistral-large-3-2512",
    name: "Mistral Large 3 (2512)",
    provider: "Mistral",
    category: "flagship",
    contextWindow: 262_000,
    maxOutput: 32_768,
    inputPrice: 0.5,
    outputPrice: 1.5,
    releaseDate: "2025-12",
    multimodal: true,
    openSource: true,
    noteworthy: "675B MoE, 262k context, open-weight",
  },
  {
    id: "mistral-medium-3",
    name: "Mistral Medium 3",
    provider: "Mistral",
    category: "mid",
    contextWindow: 131_000,
    maxOutput: 32_768,
    inputPrice: 0.4,
    outputPrice: 2,
    releaseDate: "2025-05",
    multimodal: true,
    openSource: false,
    noteworthy: "Balanced cost and quality",
  },
  {
    id: "codestral-25.01",
    name: "Codestral 25.01",
    provider: "Mistral",
    category: "mid",
    contextWindow: 256_000,
    maxOutput: 32_768,
    inputPrice: 0.3,
    outputPrice: 0.9,
    releaseDate: "2025-01",
    multimodal: false,
    openSource: true,
    noteworthy: "Code-specialized, 256k context",
  },

  // ── xAI ──
  {
    id: "grok-4",
    name: "Grok 4",
    provider: "xAI",
    category: "flagship",
    contextWindow: 131_072,
    maxOutput: 16_384,
    inputPrice: 3,
    outputPrice: 15,
    releaseDate: "2025-07",
    multimodal: true,
    openSource: false,
    noteworthy: "Advanced reasoning and coding",
  },
  {
    id: "grok-4.1-fast",
    name: "Grok 4.1 Fast",
    provider: "xAI",
    category: "mid",
    contextWindow: 2_000_000,
    maxOutput: 32_768,
    inputPrice: 0.2,
    outputPrice: 0.5,
    releaseDate: "2026-01",
    multimodal: true,
    openSource: false,
    noteworthy: "2M context, agentic tool-calling",
  },
  {
    id: "grok-3-mini",
    name: "Grok 3 mini",
    provider: "xAI",
    category: "small",
    contextWindow: 131_072,
    maxOutput: 16_384,
    inputPrice: 0.3,
    outputPrice: 0.5,
    releaseDate: "2025-02",
    multimodal: true,
    openSource: false,
  },

  // ── DeepSeek ──
  {
    id: "deepseek-r1",
    name: "DeepSeek R1",
    provider: "DeepSeek",
    category: "reasoning",
    contextWindow: 128_000,
    maxOutput: 32_768,
    inputPrice: 0.56,
    outputPrice: 1.68,
    cachedInputPrice: 0.07,
    releaseDate: "2025-01",
    multimodal: false,
    openSource: true,
    noteworthy: "Open-source reasoning leader",
  },
  {
    id: "deepseek-v3",
    name: "DeepSeek V3",
    provider: "DeepSeek",
    category: "flagship",
    contextWindow: 128_000,
    maxOutput: 16_384,
    inputPrice: 0.56,
    outputPrice: 1.68,
    cachedInputPrice: 0.07,
    releaseDate: "2025-03",
    multimodal: false,
    openSource: true,
    noteworthy: "Open-source, ultra-low cost",
  },
  {
    id: "deepseek-v3.1",
    name: "DeepSeek V3.1",
    provider: "DeepSeek",
    category: "flagship",
    contextWindow: 128_000,
    maxOutput: 16_384,
    inputPrice: 0.15,
    outputPrice: 0.75,
    releaseDate: "2025-08",
    multimodal: false,
    openSource: true,
    noteworthy: "Ultra-cheap next-gen",
  },
];

// ── Constants ────────────────────────────────────────────────

const PROVIDERS: Provider[] = [
  "OpenAI",
  "Anthropic",
  "Google",
  "Meta",
  "Mistral",
  "xAI",
  "DeepSeek",
];

const CATEGORIES: { value: Category; label: string }[] = [
  { value: "flagship", label: "Flagship" },
  { value: "mid", label: "Mid-tier" },
  { value: "small", label: "Small" },
  { value: "reasoning", label: "Reasoning" },
];

const PROVIDER_COLORS: Record<Provider, string> = {
  OpenAI: "bg-green-400/10 text-green-400 border-green-400/20",
  Anthropic: "bg-orange-400/10 text-orange-400 border-orange-400/20",
  Google: "bg-blue-400/10 text-blue-400 border-blue-400/20",
  Meta: "bg-indigo-400/10 text-indigo-400 border-indigo-400/20",
  Mistral: "bg-amber-400/10 text-amber-400 border-amber-400/20",
  xAI: "bg-zinc-300/10 text-zinc-300 border-zinc-300/20",
  DeepSeek: "bg-cyan-400/10 text-cyan-400 border-cyan-400/20",
};

const CATEGORY_LABELS: Record<Category, string> = {
  flagship: "Flagship",
  mid: "Mid-tier",
  small: "Small",
  reasoning: "Reasoning",
  embedding: "Embedding",
};

// ── Helpers ──────────────────────────────────────────────────

function formatContext(tokens: number): string {
  if (tokens >= 1_000_000) {
    const m = tokens / 1_000_000;
    return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`;
  }
  const k = tokens / 1000;
  return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`;
}

function formatPrice(price: number): string {
  if (price === 0) return "Free";
  if (price < 0.1) return `$${price.toFixed(3)}`;
  if (price < 1) return `$${price.toFixed(2)}`;
  return `$${price.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const [year, month] = dateStr.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(month!) - 1]} ${year}`;
}

type SortKey =
  | "name"
  | "provider"
  | "contextWindow"
  | "inputPrice"
  | "outputPrice"
  | "releaseDate"
  | "maxOutput";
type SortDir = "asc" | "desc";

function sortModels(models: Model[], key: SortKey, dir: SortDir): Model[] {
  return [...models].sort((a, b) => {
    let cmp = 0;
    if (key === "name" || key === "provider") {
      cmp = a[key].localeCompare(b[key]);
    } else if (key === "releaseDate") {
      cmp = a.releaseDate.localeCompare(b.releaseDate);
    } else {
      cmp = (a[key] as number) - (b[key] as number);
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

// ── Component ────────────────────────────────────────────────

export function ModelComparison() {
  const [search, setSearch] = useState("");
  const [selectedProviders, setSelectedProviders] = useState<Set<Provider>>(
    new Set(),
  );
  const [selectedCategories, setSelectedCategories] = useState<Set<Category>>(
    new Set(),
  );
  const [sortKey, setSortKey] = useState<SortKey>("inputPrice");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [showOpenSourceOnly, setShowOpenSourceOnly] = useState(false);
  const [showMultimodalOnly, setShowMultimodalOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    document.title =
      "AI Model Comparison — LLM Pricing, Context Windows & Specs — Ballah Code";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Compare GPT-5.2, Claude Opus 4.6, Gemini 3.1 Pro, Grok 4, Llama 4 and more. Interactive table of pricing, context windows, and capabilities for 30+ LLMs. Updated Feb 2026.",
      );
    }
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" || key === "provider" ? "asc" : "desc");
    }
  };

  const toggleProvider = (p: Provider) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const toggleCategory = (c: Category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedProviders(new Set());
    setSelectedCategories(new Set());
    setShowOpenSourceOnly(false);
    setShowMultimodalOnly(false);
  };

  const hasActiveFilters =
    search.length > 0 ||
    selectedProviders.size > 0 ||
    selectedCategories.size > 0 ||
    showOpenSourceOnly ||
    showMultimodalOnly;

  const filteredModels = useMemo(() => {
    let result = MODELS;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.provider.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          (m.noteworthy && m.noteworthy.toLowerCase().includes(q)),
      );
    }

    if (selectedProviders.size > 0) {
      result = result.filter((m) => selectedProviders.has(m.provider));
    }

    if (selectedCategories.size > 0) {
      result = result.filter((m) => selectedCategories.has(m.category));
    }

    if (showOpenSourceOnly) {
      result = result.filter((m) => m.openSource);
    }

    if (showMultimodalOnly) {
      result = result.filter((m) => m.multimodal);
    }

    return sortModels(result, sortKey, sortDir);
  }, [
    search,
    selectedProviders,
    selectedCategories,
    showOpenSourceOnly,
    showMultimodalOnly,
    sortKey,
    sortDir,
  ]);

  // Stats
  const stats = useMemo(() => {
    const cheapest = [...MODELS].sort((a, b) => a.inputPrice - b.inputPrice)[0];
    const largestCtx = [...MODELS].sort(
      (a, b) => b.contextWindow - a.contextWindow,
    )[0];
    const newest = [...MODELS].sort((a, b) =>
      b.releaseDate.localeCompare(a.releaseDate),
    )[0];
    return { cheapest, largestCtx, newest };
  }, []);

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column)
      return <ArrowUpDown size={11} className="text-zinc-700" />;
    return sortDir === "asc" ? (
      <ArrowUp size={11} className="text-zinc-400" />
    ) : (
      <ArrowDown size={11} className="text-zinc-400" />
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-[18px]">
        <Link
          to="/"
          className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
        >
          ballah code
        </Link>
        <div className="flex items-center gap-6 text-[13px] text-zinc-600">
          <Link to="/tools" className="hover:text-zinc-400 transition-colors">
            tools
          </Link>
          <Link to="/" className="hover:text-zinc-400 transition-colors">
            home
          </Link>
        </div>
      </nav>

      <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
          <Link to="/tools" className="hover:text-zinc-400 transition-colors">
            Tools
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-400">AI Model Comparison</span>
        </div>

        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
            AI Model Comparison
          </h1>
          <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
            Compare pricing, context windows, and capabilities across 30+ major
            LLMs. All data verified as of Feb 2026. Sort by any column,
            filter by provider or category.
          </p>
        </div>

        {/* Highlight cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-2 text-[11px] text-zinc-600 uppercase tracking-wider font-medium mb-2">
              <DollarSign size={12} /> Cheapest Input
            </div>
            <div className="text-[15px] text-zinc-200 font-medium">
              {stats.cheapest?.name}
            </div>
            <div className="text-[13px] text-zinc-500 mt-0.5">
              {formatPrice(stats.cheapest?.inputPrice ?? 0)}/M input tokens ·{" "}
              {stats.cheapest?.provider}
            </div>
          </div>
          <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-2 text-[11px] text-zinc-600 uppercase tracking-wider font-medium mb-2">
              <Layers size={12} /> Largest Context
            </div>
            <div className="text-[15px] text-zinc-200 font-medium">
              {stats.largestCtx?.name}
            </div>
            <div className="text-[13px] text-zinc-500 mt-0.5">
              {formatContext(stats.largestCtx?.contextWindow ?? 0)} tokens ·{" "}
              {stats.largestCtx?.provider}
            </div>
          </div>
          <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-2 text-[11px] text-zinc-600 uppercase tracking-wider font-medium mb-2">
              <Zap size={12} /> Most Recent
            </div>
            <div className="text-[15px] text-zinc-200 font-medium">
              {stats.newest?.name}
            </div>
            <div className="text-[13px] text-zinc-500 mt-0.5">
              {formatDate(stats.newest?.releaseDate ?? "")} ·{" "}
              {stats.newest?.provider}
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col gap-3 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search models..."
                className="w-full py-2.5 pl-9 pr-3 bg-[#111113] border border-white/[0.06] rounded-lg text-[13px] text-zinc-300 outline-none placeholder:text-zinc-700 focus:border-white/[0.12] transition-colors"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Toggle filters */}
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`py-2.5 px-4 rounded-lg border text-[13px] flex items-center gap-2 transition-colors ${showFilters || hasActiveFilters
                ? "border-white/[0.12] bg-white/[0.04] text-zinc-300"
                : "border-white/[0.06] bg-[#111113] text-zinc-500 hover:text-zinc-300"
                }`}
            >
              <Filter size={13} />
              Filters
              {hasActiveFilters && (
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                Clear all
              </button>
            )}

            <div className="ml-auto text-[12px] text-zinc-600">
              {filteredModels.length} of {MODELS.length} models
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-4 flex flex-col gap-4">
              {/* Providers */}
              <div>
                <div className="text-[11px] text-zinc-600 uppercase tracking-wider font-medium mb-2">
                  Provider
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PROVIDERS.map((p) => (
                    <button
                      key={p}
                      onClick={() => toggleProvider(p)}
                      className={`px-2.5 py-1 rounded-md text-[12px] border transition-colors ${selectedProviders.has(p)
                        ? PROVIDER_COLORS[p]
                        : "border-white/[0.06] text-zinc-600 hover:text-zinc-400 hover:border-white/[0.1]"
                        }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div>
                <div className="text-[11px] text-zinc-600 uppercase tracking-wider font-medium mb-2">
                  Category
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => toggleCategory(c.value)}
                      className={`px-2.5 py-1 rounded-md text-[12px] border transition-colors ${selectedCategories.has(c.value)
                        ? "border-violet-400/30 bg-violet-400/10 text-violet-400"
                        : "border-white/[0.06] text-zinc-600 hover:text-zinc-400 hover:border-white/[0.1]"
                        }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-[12px] text-zinc-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOpenSourceOnly}
                    onChange={(e) => setShowOpenSourceOnly(e.target.checked)}
                    className="accent-violet-400"
                  />
                  Open source only
                </label>
                <label className="flex items-center gap-2 text-[12px] text-zinc-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showMultimodalOnly}
                    onChange={(e) => setShowMultimodalOnly(e.target.checked)}
                    className="accent-violet-400"
                  />
                  Multimodal only
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {(
                  [
                    { key: "name" as SortKey, label: "Model", align: "left" },
                    {
                      key: "provider" as SortKey,
                      label: "Provider",
                      align: "left",
                    },
                    {
                      key: "contextWindow" as SortKey,
                      label: "Context",
                      align: "right",
                    },
                    {
                      key: "maxOutput" as SortKey,
                      label: "Max Output",
                      align: "right",
                    },
                    {
                      key: "inputPrice" as SortKey,
                      label: "Input $/M",
                      align: "right",
                    },
                    {
                      key: "outputPrice" as SortKey,
                      label: "Output $/M",
                      align: "right",
                    },
                    {
                      key: "releaseDate" as SortKey,
                      label: "Released",
                      align: "right",
                    },
                  ] as { key: SortKey; label: string; align: string }[]
                ).map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`py-3 px-4 text-[11px] text-zinc-600 uppercase tracking-wider font-medium cursor-pointer hover:text-zinc-400 transition-colors select-none whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"
                      }`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label} <SortIcon column={col.key} />
                    </span>
                  </th>
                ))}
                <th className="py-3 px-4 text-[11px] text-zinc-600 uppercase tracking-wider font-medium text-center whitespace-nowrap">
                  Features
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredModels.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-16 text-center text-zinc-600 text-[14px]"
                  >
                    No models match your filters.{" "}
                    <button
                      onClick={clearFilters}
                      className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
                    >
                      Clear filters
                    </button>
                  </td>
                </tr>
              ) : (
                filteredModels.map((model, i) => (
                  <tr
                    key={model.id}
                    className={`hover:bg-white/[0.02] transition-colors ${i < filteredModels.length - 1
                      ? "border-b border-white/[0.04]"
                      : ""
                      }`}
                  >
                    {/* Name */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-zinc-200 font-medium whitespace-nowrap">
                          {model.name}
                        </span>
                        {model.noteworthy && (
                          <span className="text-[11px] text-zinc-600">
                            {model.noteworthy}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Provider */}
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${PROVIDER_COLORS[model.provider]}`}
                      >
                        {model.provider}
                      </span>
                    </td>

                    {/* Context */}
                    <td className="py-3 px-4 text-right font-mono text-zinc-300 whitespace-nowrap">
                      {formatContext(model.contextWindow)}
                    </td>

                    {/* Max Output */}
                    <td className="py-3 px-4 text-right font-mono text-zinc-500 whitespace-nowrap">
                      {formatContext(model.maxOutput)}
                    </td>

                    {/* Input Price */}
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <span className="font-mono text-zinc-300">
                        {formatPrice(model.inputPrice)}
                      </span>
                      {model.cachedInputPrice !== undefined && (
                        <div className="text-[10px] text-zinc-600 mt-0.5">
                          {formatPrice(model.cachedInputPrice)} cached
                        </div>
                      )}
                    </td>

                    {/* Output Price */}
                    <td className="py-3 px-4 text-right font-mono text-zinc-300 whitespace-nowrap">
                      {formatPrice(model.outputPrice)}
                    </td>

                    {/* Release Date */}
                    <td className="py-3 px-4 text-right text-zinc-500 whitespace-nowrap">
                      {formatDate(model.releaseDate)}
                    </td>

                    {/* Features */}
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        {model.multimodal && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-400/10 text-blue-400 border border-blue-400/20"
                            title="Multimodal (text + vision)"
                          >
                            👁
                          </span>
                        )}
                        {model.openSource && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-400/10 text-green-400 border border-green-400/20"
                            title="Open source / open weights"
                          >
                            OSS
                          </span>
                        )}
                        {model.category === "reasoning" && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-400/10 text-amber-400 border border-amber-400/20"
                            title="Extended reasoning / thinking"
                          >
                            🧠
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mt-4 text-[11px] text-zinc-600 px-1">
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-blue-400/10 text-blue-400 border border-blue-400/20 text-[10px]">
              👁
            </span>
            Multimodal
          </span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-green-400/10 text-green-400 border border-green-400/20 text-[10px]">
              OSS
            </span>
            Open source
          </span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400 border border-amber-400/20 text-[10px]">
              🧠
            </span>
            Reasoning
          </span>
          <span className="ml-auto">
            Prices per 1M tokens · Updated Feb 2026
          </span>
        </div>

        {/* Cross-link to Token Counter */}
        <div className="mt-8 bg-[#111113] border border-white/[0.06] rounded-xl p-5 flex items-center justify-between">
          <div>
            <div className="text-[14px] text-zinc-300 font-medium mb-1">
              Want to estimate actual costs?
            </div>
            <p className="text-[13px] text-zinc-500">
              Paste your text into the token counter to see exact token counts
              and cost estimates for each model.
            </p>
          </div>
          <Link
            to="/tools/llm-token-counter"
            className="shrink-0 ml-6 py-2 px-4 rounded-lg border border-white/[0.08] bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06] hover:text-zinc-200 transition-colors text-[13px] flex items-center gap-1.5"
          >
            Token Counter <ExternalLink size={12} />
          </Link>
        </div>

        {/* SEO content */}
        <section className="mt-20 max-w-2xl">
          <h2 className="text-xl font-medium tracking-[-0.02em] mb-4 text-zinc-300">
            How to choose the right LLM
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-500 leading-[1.75]">
            <p>
              Choosing the right large language model depends on your use case,
              budget, and latency requirements. There's no single "best" model —
              the right choice is a{" "}
              <strong className="text-zinc-400">tradeoff</strong> between
              capability, speed, and cost.
            </p>
            <p>
              <strong className="text-zinc-400">Flagship models</strong> like
              GPT-5.2, Claude Opus 4.6, and Gemini 2.5 Pro offer the highest
              capability but cost more per token. Use them for complex
              reasoning, code generation, and tasks where quality matters more
              than speed.
            </p>
            <p>
              <strong className="text-zinc-400">Mid-tier models</strong> like
              Claude Sonnet 4.5, GPT-4.1 mini, and Gemini 2.5 Flash balance
              quality and cost. They're ideal for production workloads where you
              need good results without paying flagship prices.
            </p>
            <p>
              <strong className="text-zinc-400">Small models</strong> like GPT-5
              mini, Claude Haiku 4.5, and GPT-4.1 nano are optimized for speed
              and cost. Use them for classification, extraction, simple Q&A, or
              as a fast first-pass before escalating to a larger model.
            </p>
            <p>
              <strong className="text-zinc-400">Reasoning models</strong> like
              o3, o4-mini, GPT-5.2 Pro, and DeepSeek R1 use extended thinking to
              solve harder problems — math, logic, and multi-step planning. They
              cost more but can outperform flagship models on difficult tasks.
            </p>
          </div>

          <h2 className="text-xl font-medium tracking-[-0.02em] mb-4 mt-12 text-zinc-300">
            What is context window size?
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-500 leading-[1.75]">
            <p>
              The <strong className="text-zinc-400">context window</strong> is
              the maximum number of tokens a model can process in a single
              request — including both input and output. A 128k context window
              can hold roughly 100,000 words or a medium-sized codebase.
            </p>
            <p>
              Models with 1M+ context windows (GPT-4.1, Gemini 2.5, Llama 4
              Maverick) can process entire repositories, long documents, or
              extended conversation histories without truncation. However,
              larger context doesn't always mean better retrieval — performance
              can degrade on information buried in the middle of very long
              contexts.
            </p>
          </div>

          <h2 className="text-xl font-medium tracking-[-0.02em] mb-4 mt-12 text-zinc-300">
            Understanding LLM pricing
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-500 leading-[1.75]">
            <p>
              LLM APIs charge{" "}
              <strong className="text-zinc-400">per token</strong>, with
              separate rates for input (prompt) and output (completion) tokens.
              Output tokens are typically 3-8x more expensive than input tokens.
            </p>
            <p>
              Many providers offer{" "}
              <strong className="text-zinc-400">cached input pricing</strong> —
              if part of your prompt is the same across requests (like a system
              prompt or large document), subsequent requests charge the cached
              rate, which is typically 75% cheaper.
            </p>
            <p>
              For AI coding tools like Ballah Code, smart caching and model
              routing can reduce costs dramatically. By remembering context
              across sessions and choosing the right model for each sub-task,
              you can get flagship-quality results at mid-tier prices.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
        <Link to="/" className="hover:text-zinc-600 transition-colors">
          ballah code
        </Link>
        <span>ballah tech</span>
      </footer>
    </div>
  );
}
